import { Star } from "lucide-react";
import { IMAGES, REVIEWS, SITE } from "@/lib/site";

function GoogleG({ className = "w-5 h-5" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20.4H24v7.2h11.2c-1.5 4.2-5.5 7.2-11.2 7.2-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.9 6 29.2 4 24 4 13 4 4 13 4 24s9 20 20 20c11 0 20-9 20-20 0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l5.9 4.3C13.9 15 18.6 12 24 12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.9 6 29.2 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.1 0 9.8-1.9 13.3-5.1l-6.1-5c-2 1.4-4.5 2.2-7.2 2.2-5.6 0-10.3-3-11.9-7.1l-6 4.6C9.5 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20.4H24v7.2h11.2c-.7 2-2 3.7-3.7 5l6.1 5C41.1 34.6 44 29.6 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function Reviews() {
  const hasRating = Boolean(SITE.googleRating);
  const hasReviews = Array.isArray(REVIEWS) && REVIEWS.length > 0;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 lg:py-24 overflow-hidden border-t border-white/5"
    >
      <img
        src={IMAGES.texture}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/85" />
      <div className="absolute inset-0 es-noise opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4A84B]" />
              <span className="es-label text-[#D4A84B]">From the Neighborhood</span>
            </div>
            <h2 className="font-serif-display text-white text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              What our neighbors say.
            </h2>
            <p className="text-white/65 text-sm sm:text-base mt-5 leading-relaxed">
              Real reviews from our Google Business Profile.
            </p>
          </div>

          {hasRating && (
            <div className="flex items-center gap-4" data-testid="google-rating">
              <a
                href={SITE.googleReviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Everstone Roofing on Google"
                data-testid="google-rating-link"
              >
                <GoogleG className="w-9 h-9" />
              </a>
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-white text-2xl font-serif-display font-semibold mr-1">{SITE.googleRating}</span>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D4A84B] text-[#D4A84B]" />
                    ))}
                  </div>
                </div>
                <p className="es-label text-white/60 mt-1">on Google</p>
              </div>
            </div>
          )}
        </div>

        {hasReviews ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {REVIEWS.map((r, i) => (
              <article
                key={i}
                data-testid={`review-card-${i}`}
                className="relative bg-[#0E0E0E]/80 backdrop-blur-sm border border-[#2E3036] p-8 hover:border-[#D4A84B]/50 transition-colors duration-500"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-0.5">
                    {[...Array(r.stars || 5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#D4A84B] text-[#D4A84B]" />
                    ))}
                  </div>
                  <GoogleG className="w-5 h-5" />
                </div>
                <p className="font-serif-display text-white text-lg leading-snug">"{r.text}"</p>
                <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                  <p className="text-white font-semibold text-sm">{r.name}</p>
                  <p className="text-white/45 text-xs">Google review</p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div
            data-testid="reviews-empty-state"
            className="bg-[#0E0E0E]/80 backdrop-blur-sm border border-dashed border-[#D4A84B]/40 p-10 lg:p-14 text-center"
          >
            <GoogleG className="w-10 h-10 mx-auto mb-5" />
            <h3 className="font-serif-display text-white text-2xl sm:text-3xl leading-tight">
              Share your experience on Google.
            </h3>
            <p className="text-white/65 text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
              We only publish real reviews from our Google Business Profile. If we've worked on
              your roof, we'd be grateful for a few honest words — it helps other Wisconsin
              homeowners find us.
            </p>
            <a
              href={SITE.googleLeaveReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-cta-leave"
              className="inline-flex items-center gap-2 mt-7 es-btn-primary"
            >
              Leave a Google review
            </a>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href={SITE.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-cta-readall"
            className="es-btn-secondary"
          >
            Read all reviews on Google
          </a>
          {hasReviews && (
            <a
              href={SITE.googleLeaveReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-cta-leave-bottom"
              className="es-btn-primary"
            >
              Leave us a review
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
